import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SignedImage } from "@/lib/signedFile";
import { toast } from "sonner";
import { Loader2, Upload, X, Image as ImageIcon } from "lucide-react";

/**
 * Reusable avatar/logo uploader. Uploads to /api/upload with a validated
 * server-side context and stores the returned `file_id` (not a URL) in the
 * parent's field. The preview is rendered through <SignedImage /> which
 * requests a short-lived signed URL from the backend.
 */
export default function AvatarUploader({
  value,
  onChange,
  size = 96,
  shape = "square",
  fallbackIcon: FallbackIcon = ImageIcon,
  testid = "avatar-uploader",
  context = "avatar",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const rounded = shape === "circle" ? "rounded-full" : "rounded-2xl";

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("يرجى اختيار صورة صالحة");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("حجم الصورة يجب أن يكون أقل من 25 ميغابايت");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("context", context);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.file_id);
      toast.success("تم رفع الصورة");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <div
        className={`${rounded} bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white overflow-hidden shrink-0 relative`}
        style={{ height: size, width: size }}
      >
        {value ? (
          <SignedImage
            fileId={value}
            alt="الشعار"
            className="h-full w-full object-cover"
            fallback={<FallbackIcon className="h-10 w-10" strokeWidth={1.5} />}
          />
        ) : (
          <FallbackIcon className="h-10 w-10" strokeWidth={1.5} />
        )}
        {uploading && (
          <div className="absolute inset-0 bg-slate-900/60 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60 transition-colors"
            data-testid={`${testid}-btn`}
          >
            <Upload className="h-4 w-4" />
            {value ? "استبدال الصورة" : "رفع صورة"}
          </button>
          {value && !uploading && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid={`${testid}-clear`}
            >
              <X className="h-4 w-4" /> إزالة
            </button>
          )}
        </div>
        <div className="mt-2 text-xs text-slate-500">JPG أو PNG أو WEBP، بحد أقصى 25 ميغابايت.</div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
      </div>
    </div>
  );
}
